import React, { useMemo } from 'react';
import { useResetRecoilState } from 'recoil';
import { navigate } from 'gatsby';
import { Badge, Label } from '@nooz-optics/nooz-components-lib';
import { ProductPrice } from '../../UI/Elements/Price/productPrice';
import {
  returnCurrencyCharFromCode,
  returnLocalisedPath,
} from '../../../utils/globals/index';
import { Image } from '../../UI/Media/Image/index';
import { NativeSelect } from '../../UI/Forms/Inputs';
import { sidebarState } from '../../../utils/recoil/sidebar';
import { withLocale } from '../../../utils/hocs';
import { returnProductSlugUrl } from '../Shop/ProductItem/shopProductItem.utils';
import { drawerState } from 'utils/recoil/drawer';

const QUANTITY_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const LineOrderItem = withLocale(
  ({
    item,
    image,
    price,
    compareAtPrice,
    handleLineItemRemove,
    handleLineItemQuantityChange,
    removeLabel,
    locale,
  }) => {
    const color = useMemo(() => {
      const c = item?.variantsCollection?.items?.find((v) =>
        item.variant?.sku?.includes(v.title)
      );
      if (c) return c?.color?.shopifyKey?.toLowerCase();
      return null;
    }, [item]);

    const resetDrawer = useResetRecoilState(drawerState);

    const returnLineItemOptions = () =>
      QUANTITY_OPTIONS.map((option) => ({
        value: option,
        label: { text: option },
      }));

    const currency = returnCurrencyCharFromCode(
      item.variant?.priceV2.currencyCode
    );

    const quantityForPackItemGreaterThanOne =
      !handleLineItemQuantityChange && item.quantity > 1;

    const handleOnLineItemClick = () => {
      if (item?.slug) {
        resetDrawer();
        // Navigate to the product page
        navigate(
          returnLocalisedPath(
            locale[0],
            returnProductSlugUrl(item.slug, item.variant)
          )
        );
      }
    };

    return (
      <div className='flex py-4'>
        <div className='flex-shring-0'>
          <div className='w-24 h-24 rounded-md border overflow-hidden object-center object-cover'>
            <Image
              width='100%'
              height='100%'
              objectFit='cover'
              description={image?.altText}
              media={{ file: { url: image.src } }}
            />
          </div>
        </div>
        <div className='ml-4 flex-1 flex flex-col justify-between lg:ml-6'>
          <div className='flex justify-between md:grid md:grid-cols-2'>
            <div className='pr-6'>
              {item.categories && (
                <Badge
                  size='sm'
                  type='white'
                  dotColor={item.categories[0].color}
                  label={item.categories[0].label}
                />
              )}
              <button
                className='mt-1'
                type='button'
                onClick={handleOnLineItemClick}>
                <Label
                  {...item?.label}
                  text={item?.label?.text || item.title}
                  color={{ classname: 'gray-800' }}
                  size='lg'
                  weight='medium'
                  transform='capitalize underline'
                />
              </button>
              {item.collection && (
                <div className='mt-1'>
                  <Label
                    text={item.collection.title}
                    transform='capitalize'
                    size='sm'
                  />
                </div>
              )}
              {color && (
                <div className='mt-1'>
                  <Label
                    text={color || ''}
                    transform='capitalize'
                    size='sm'
                  />
                </div>
              )}
            </div>
            <div
              className={'flex flex-col-reverse md:flex-row justify-between'}>
              <div className='mt-4 md:mt-0 mr-0 md:mr-4'>
                {handleLineItemQuantityChange && (
                  <div className='w-max'>
                    <NativeSelect
                      selectedValue={item.quantity}
                      values={returnLineItemOptions()}
                      color={{ classname: 'primary' }}
                      size='base'
                      type='outlined'
                      onChange={(e) =>
                        handleLineItemQuantityChange(item, e.target.value)
                      }
                    />
                  </div>
                )}
                {handleLineItemRemove && (
                  <div
                    className='cursor-pointer mt-2 hover:underline'
                    onClick={handleLineItemRemove}>
                    <Label
                      {...removeLabel}
                      size='sm'
                      icon={{
                        iconKey: 'IoMdTrash',
                        color: { classname: 'gray-400' },
                      }}
                    />
                  </div>
                )}
              </div>
              {price && (
                <ProductPrice
                  productCategory={item?.categories && item.categories[0]}
                  price={{ text: price.amount }}
                  compareAtPrice={compareAtPrice && { text: compareAtPrice }}
                  currency={{ text: currency }}
                  size='xl'
                />
              )}
            </div>
          </div>
          {quantityForPackItemGreaterThanOne && (
            <Label
              size='sm'
              text={`X ${item.quantity}`}
            />
          )}
        </div>
      </div>
    );
  }
);
